import { useState } from "react";
import {
  TABLE_ACTIONS,
  SortOrder,
} from "helpers/constants";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import CustomTable from "components/Table/CustomTable";
import { Campaign } from "types/campaign";
import CreateOrEditCampaignModal from "modals/CreateOrEditCampaignModal/CreateOrEditCampaignModal";
import { MainLayoutContainer } from "components/Layout";
import { Add } from "@mui/icons-material";
import { ResponsiveButton } from "components/Button";
import { Chip, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useCampaign } from "./useCampaign";

export default function CampaignList() {
  const navigate = useNavigate();
  const [createCampaignModal, setCreateCampaignModal] = useState(false);
  const [deleteCampaignModal, setDeleteCampaignModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const {
    campaigns,
    loading,
    organizationId,
    error,
    deleteCampaignOpen,
    campaignIdToDelete,
    handleActionClick: handleActionClickBase,
    setDeleteCampaignOpen,
    handleDelete,
    refetch,
  } = useCampaign();

  const handleActionClick = (action: TABLE_ACTIONS, id: string) => {
    if (action === TABLE_ACTIONS.EDIT) {
      const campaignToEdit = campaigns?.find((campaign) => campaign.campaign_id === id);
      if (campaignToEdit) {
        setSelectedCampaign(campaignToEdit);
        setCreateCampaignModal(true);
      }
    } else {
      handleActionClickBase(action, id);
      if (action === TABLE_ACTIONS.DELETE) {
        setDeleteCampaignModal(true);
      }
    }
  };

  const handleCreateCampaignModalOpen = () => {
    setCreateCampaignModal(true);
  };

  const handleCreateOrEditCampaignChange = async () => {
    if (selectedCampaign !== null) {
      setSelectedCampaign(null);
    }
    setCreateCampaignModal(false);
    if (refetch && typeof refetch === 'function') {
      await refetch();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <MainLayoutContainer
      title="Campaigns"
      loading={loading}
      error={error}
      buttonActions={
        <ResponsiveButton
          onClick={() => {
            if (organizationId) {
              navigate(`/organization/${organizationId}/campaign/create`);
            } else {
              handleCreateCampaignModalOpen();
            }
          }}
          icon={<Add />}
        >
          New Campaign
        </ResponsiveButton>
      }
    >
      <CustomTable
        entities={campaigns}
        handleActionClick={handleActionClick}
        fieldToFilter={"name"}
        columns={[
          {
            id: "name",
            label: "Name",
            value: "name",
          },
          {
            id: "start_ts",
            label: "Start Date",
            value: (row: Campaign) => formatDate(row.start_ts),
          },
          {
            id: "end_ts",
            label: "End Date",
            value: (row: Campaign) => formatDate(row.end_ts),
          },
          {
            id: "frequency",
            label: "Frequency",
            value: "frequency",
          },
          {
            id: "surveys",
            label: "Surveys",
            value: (row: Campaign) => row.surveys?.length || 0,
          },
          {
            id: "users",
            label: "Users",
            value: (row: Campaign) => row.users?.length || 0,
          },
          {
            id: "should_send_email",
            label: "Send Email",
            value: (row: Campaign) => (
              <Chip
                label={row.should_send_email ? "Yes" : "No"}
                color={row.should_send_email ? "success" : "default"}
                size="small"
              />
            ),
          },
          { id: "actions", value: "" },
        ]}
        isSelectable={false}
        allowedMenuItems={[TABLE_ACTIONS.EDIT, TABLE_ACTIONS.DELETE]}
        extraActionItems={[]}
        idColumn={"campaign_id"}
        sortDefault={SortOrder.desc}
        sortDefaultField={"created_at"}
      />

      {createCampaignModal && (
        <CreateOrEditCampaignModal
          handleCreateOrEditCampaignChange={handleCreateOrEditCampaignChange}
          createOrEditCampaignModal={createCampaignModal}
          handleCreateOrEditCampaignModalDismiss={() => {
            setCreateCampaignModal(false);
            if (selectedCampaign !== null) {
              setSelectedCampaign(null);
            }
          }}
          campaign={selectedCampaign}
        />
      )}

      {deleteCampaignModal && (
        <ConfirmActionModal
          title={"Heads up!"}
          message={"Are you sure you want to delete this campaign?"}
          show={deleteCampaignModal}
          onConfirm={() => {
            handleDelete();
            setDeleteCampaignModal(false);
          }}
          onDismiss={() => {
            setDeleteCampaignModal(false);
          }}
        />
      )}
    </MainLayoutContainer>
  );
}
